import axios from 'axios';
import Cookies from 'js-cookie';

const TOKEN_REFRESH_URL = '/api/token/refresh/';

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function updateStoredToken(newAccessToken) {
  const user = getStoredUser();
  if (user) {
    user.token = newAccessToken;
    user.access_token = newAccessToken;
    localStorage.setItem('user', JSON.stringify(user));
    Cookies.set('access_token', newAccessToken, {
      expires: 1 / 24,
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:',
    });
  }
}

// Request interceptor: ALWAYS inject latest token from localStorage
axios.interceptors.request.use(
  (config) => {
    const user = getStoredUser();
    const token = user?.access_token || user?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: refresh token on 401 and retry
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for the refresh endpoint itself to avoid infinite loop
    if (originalRequest.url === TOKEN_REFRESH_URL) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for the ongoing refresh, then retry with new token
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(originalRequest));
          });
        });
      }

      isRefreshing = true;
      const user = getStoredUser();
      const refreshTokenValue = user?.refresh_token;

      if (!refreshTokenValue) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const resp = await fetch(TOKEN_REFRESH_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshTokenValue }),
        });
        const data = await resp.json();

        if (resp.ok && data.access) {
          console.log('[axios-config] Token refreshed, new token:', data.access.substring(0, 20) + '...');
          updateStoredToken(data.access);
          onRefreshed(data.access);
          // Explicitly set the new token on the retry request config
          const retryConfig = {
            ...originalRequest,
            headers: {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${data.access}`,
            },
          };
          delete retryConfig._retry;
          return axios(retryConfig);
        } else {
          // Refresh failed — clear auth and reject
          localStorage.removeItem('user');
          Cookies.remove('access_token');
          Cookies.remove('refresh_token');
          return Promise.reject(error);
        }
      } catch (refreshErr) {
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
